import * as React from 'react';

import { compose } from 'recompose';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import type { InjectedNaptime } from 'bundles/naptimejs';
import Naptime from 'bundles/naptimejs';
import type EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import ProgramMembershipsV2 from 'bundles/naptimejs/resources/programMemberships.v2';
import type ThirdPartyOrganizationsV1Type from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import AutoenrolledCoursesModal from 'bundles/program-home/components/modals/AutoenrolledCoursesModal';
import ForcedSSORecoveryEmailModal from 'bundles/program-home/components/modals/ForcedSSORecoveryEmailModal';
import ProgramAccessModal from 'bundles/program-home/components/modals/ProgramAccessModal';
import ProgramAddEmailModal from 'bundles/program-home/components/modals/ProgramAddEmailModal';
import ProgramAlternativeEmailModal from 'bundles/program-home/components/modals/ProgramAlternativeEmailModal';
import ProgramLearnerApiManager from 'bundles/program-home/utils/ProgramLearnerApiManager';
import useHasThirdPartyOrgEnforcedSSOLogin from 'bundles/saml-sso/shared/hooks/useHasThirdPartyOrgEnforcedSSOLogin';
import { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';
import { useEnterpriseAutoEnrolledCourses } from 'bundles/unified-common/contexts/EnterpriseAutoEnrolledCoursesContext';
import { useEnterpriseModalManager } from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useIsGwGProgram,
  useProgram,
  useProgramMembership,
  useUserEmails,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

type Props = {
  naptime: InjectedNaptime;
};

const EnterpriseModalManager = ({ naptime }: Props) => {
  const router = useRouter();
  const modalManager = useEnterpriseModalManager();
  const program = useProgram();
  const programMembership = useProgramMembership();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const isGwGProgram = useIsGwGProgram();
  const userEmails = useUserEmails();
  const autoEnrolledCourses = useEnterpriseAutoEnrolledCourses();
  const features = useEnterpriseProductFeatures();
  const { email_address: emailAddress } = user.get();
  const { isSsoLoginForced } = useHasThirdPartyOrgEnforcedSSOLogin(thirdPartyOrganization?.id || '', emailAddress);

  if (!program || !thirdPartyOrganization) {
    return null;
  }

  const apiManager = new ProgramLearnerApiManager({ program, naptime });
  if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.PROGRAM_ACCESS) {
    return (
      <ProgramAccessModal
        apiManager={apiManager}
        isAccessModalDismissed={modalManager?.isAccessModalDismissed}
        isProgramAdmin={!!programMembership.isProgramAdmin}
        onCloseAccessModal={() => modalManager?.setIsAccessModalDismissed?.(true)}
        program={
          {
            ...program,
            name: program?.metadata?.name,
            slug: program?.metadata?.slug,
          } as EnterpriseProgramsV1
        }
        programMembership={new ProgramMembershipsV2(programMembership?.membership ?? {})}
        reloadProgramMembership={() => {}}
        router={router}
        thirdPartyOrganization={thirdPartyOrganization as ThirdPartyOrganizationsV1Type}
        userId={user.get()?.id}
        // Not used in component.
        isUpswell={false}
        // Not used in component.
        isNonBlacklistedC4cv={false}
        shouldUseJoinTrackingVerbiage={!!features?.shouldUseJoinTrackingVerbiage}
        isGwGProgram={isGwGProgram}
        programRecommendationsAvailableCoursesCopy={!!features?.programRecommendationsAvailableCoursesCopy}
        autoEnrolledCoursesWithData={autoEnrolledCourses}
      />
    );
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.LOGIN) {
    router.push({
      pathname: router.location.pathname,
      params: router.params,
      query: { ...router.location.query, authMode: 'login' },
    });

    modalManager.setHasShownLoginModal(true);

    return null;
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.AUTOENROLLED_COURSES && autoEnrolledCourses) {
    return (
      <AutoenrolledCoursesModal
        userId={user.get()?.id}
        autoEnrolledCoursesWithData={autoEnrolledCourses}
        setIsAutoenrolledCoursesModalDismissed={modalManager?.setIsAutoenrolledCoursesModalDismissed || (() => {})}
        programId={program.id}
        programSlug={program.metadata.slug}
      />
    );
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.ADD_EMAIL) {
    if (isSsoLoginForced && userEmails?.length) {
      return (
        <ForcedSSORecoveryEmailModal
          programId={program.id}
          userEmails={userEmails}
          userId={user.get()?.id}
          router={router}
        />
      );
    }
    return (
      <>
        <ProgramAddEmailModal
          apiManager={apiManager}
          programId={program.id}
          programName={program.metadata.name}
          userId={user.get()?.id}
          thirdPartyOrganizationId={thirdPartyOrganization.id}
        />
        {userEmails && (
          <ProgramAlternativeEmailModal
            apiManager={apiManager}
            programId={program.id}
            programName={program.metadata.name}
            userEmails={userEmails}
            userId={user.get()?.id}
            thirdPartyOrganizationId={thirdPartyOrganization.id}
          />
        )}
      </>
    );
  } else {
    return null;
  }
};

const EnterpriseModalManagerWithErrorBoundary = ({ naptime }: Props) => {
  return (
    <ErrorBoundaryWithTagging componentName="EnterpriseModalManager">
      <EnterpriseModalManager naptime={naptime} />
    </ErrorBoundaryWithTagging>
  );
};

export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterpriseModalManagerWithErrorBoundary);
